import { Link } from 'react-router-dom';
import type { SolMyDaySourceCard as SolMyDaySourceCardData } from '../../data/sol/sol-contract';
import { formatInstant } from '../../lib/timezone';
import { MY_DAY_PATH } from '../my-day/my-day-route';

/**
 * The cited FOLLOW-UP, as a card that opens My Day. M12 / S12-03.
 *
 * **A DAY-ONLY FOLLOW-UP SHOWS ITS DAY AND NOTHING ELSE.** There is no o'clock
 * to render and — because `dueTime` and `dueAt` are null on that shape — nothing
 * here an oversight could invent one from. A timed one is rendered in the
 * canonical Crewlink timezone the server bucketed it in, which is the same zone
 * the My Day screen uses, so a card and the list can never disagree about when.
 *
 * **IT OPENS MY DAY, NOT THE FOLLOW-UP.** Sol cannot act on a follow-up in this
 * slice and the card carries no row id, so there is nothing to open one WITH —
 * and a link that pretended otherwise would promise something Crewlink cannot
 * do.
 */
export interface SolMyDaySourceCardProps {
  readonly card: SolMyDaySourceCardData;
}

/** Overdue / Today / Upcoming — **the server's word, shown as it came.** */
const BUCKET_LABEL: Readonly<Record<SolMyDaySourceCardData['bucket'], string>> = {
  overdue: 'Overdue',
  today: 'Today',
  upcoming: 'Upcoming',
};

export function SolMyDaySourceCard({ card }: SolMyDaySourceCardProps) {
  // **THE TIMED RENDERING USES THE ZONE THE BUCKET WAS DECIDED IN**, which the
  // server sent with the row. No second timezone interpretation exists here.
  const when =
    card.scheduleKind === 'timed' && card.dueAt !== null
      ? formatInstant(card.dueAt, card.timeZone, {
          dateStyle: 'medium',
          timeStyle: 'short',
        }) || card.dueDate
      : card.dueDate;

  const details = [
    card.projectName,
    card.captureNumber === null ? null : `CAP-${String(card.captureNumber).padStart(4, '0')}`,
    card.description,
  ].filter((line): line is string => line !== null && line !== '');

  return (
    <Link
      className="sol-source sol-source--my-day"
      to={MY_DAY_PATH}
      aria-label={`Open My Day for ${card.title}`}
    >
      <span className="sol-source-head">
        <span className="sol-source-title">{card.title}</span>
        <span className="sol-source-status">{BUCKET_LABEL[card.bucket]}</span>
      </span>
      <span className="sol-source-meta">{when}</span>
      {details.length > 0 ? (
        <span className="sol-source-caption">{details.join(' · ')}</span>
      ) : null}
      <span className="sol-source-open" aria-hidden="true">
        Open My Day →
      </span>
    </Link>
  );
}
